/* eslint-disable react/prop-types */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import saveAs from 'file-saver';

import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';
import {
  EditingState,
  PagingState,
  SortingState,
  IntegratedSorting,
  SearchState,
  SelectionState,
  IntegratedSelection,
  CustomPaging,
  DataTypeProvider
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';

import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditColumn,
  PagingPanel,
  TableColumnResizing,
  DragDropProvider,
  TableColumnReordering,
  SearchPanel,
  Toolbar,
  TableSelection,
  ColumnChooser,
  TableColumnVisibility,
  ExportPanel,
  TableInlineCellEditing
} from '@devexpress/dx-react-grid-material-ui';

import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import ItemDialogContent from './ItemDialogContent';
import ImageFullScreenDialog from '../../../shared/dialog/ImageFullScreenDialog';

import HeaderFilterCell, {
  getRowCellValue,
  HeaderFilterContext
} from '../../CustomerNew/table/customTemplate/HeaderFilterCell';
import itemService from '../../../services/ItemService';
import { toast } from 'react-hot-toast';
import KebabCommandCell from './customTemplate/KebabCommandCell';
import CustomToolbarRoot from './customTemplate/CustomToolbarRoot';
import ResponsiveTableContainer, { StickyTableHead } from '../../common/ResponsiveTableContainer';
import { meaningfulItemChanges, sparseItemUpdate } from './helper';

/* eslint-disable no-shadow */

const Popup = ({ row, onChange, onApplyChanges, onCancelChanges, open }) => {
  if (!open || !row) return null;
  return (
    <Dialog open={open} onClose={onCancelChanges} aria-labelledby="form-dialog-title" fullWidth maxWidth="md">
      <ItemDialogContent
        row={row}
        onCancelChanges={onCancelChanges}
        onChange={onChange}
        onApplyChanges={onApplyChanges}
      />
    </Dialog>
  );
};

// eslint-disable-next-line react/display-name
const PopupEditing = React.memo(({ popupComponent: Popup }) => (
  <Plugin>
    <Template name="popupEditing">
      <TemplateConnector>
        {(
          { rows, getRowId, addedRows, editingRowIds, createRowChange, rowChanges },
          {
            changeRow,
            changeAddedRow,
            commitChangedRows,
            commitAddedRows,
            stopEditRows,
            cancelAddedRows,
            cancelChangedRows
          }
        ) => {
          const isNew = addedRows.length > 0;
          let editedRow;
          let rowId;
          if (isNew) {
            rowId = 0;
            editedRow = addedRows[rowId];
          } else {
            [rowId] = editingRowIds;
            const targetRow = rows.filter((row) => getRowId(row) === rowId)[0];
            editedRow = { ...targetRow, ...rowChanges[rowId] };
          }

          const processValueChange = ({ target: { name, value } }) => {
            const changeArgs = {
              rowId,
              change: createRowChange(editedRow, value, name)
            };
            if (isNew) {
              changeAddedRow(changeArgs);
            } else {
              changeRow(changeArgs);
            }
          };
          const rowIds = isNew ? [0] : editingRowIds;
          const applyChanges = () => {
            if (isNew) {
              commitAddedRows({ rowIds });
            } else {
              stopEditRows({ rowIds });
              commitChangedRows({ rowIds });
            }
          };
          const cancelChanges = () => {
            if (isNew) {
              cancelAddedRows({ rowIds });
            } else {
              stopEditRows({ rowIds });
              cancelChangedRows({ rowIds });
            }
          };

          const open = editingRowIds.length > 0 || isNew;
          return (
            <Popup
              open={open}
              row={editedRow}
              onChange={processValueChange}
              onApplyChanges={applyChanges}
              onCancelChanges={cancelChanges}
            />
          );
        }}
      </TemplateConnector>
    </Template>
    <Template name="root">
      <TemplatePlaceholder />
      <TemplatePlaceholder name="popupEditing" />
    </Template>
  </Plugin>
));

const ITEM_INLINE_COLUMNS = ['Active', 'Sku', 'Description', 'UnitPrice', 'PurchaseDesc', 'PurchaseCost'];
const ITEM_FILTER_VALUES = { Active: ['false', 'true'] };

const ActiveFormatter = ({ value }) => (value === false ? 'Inactive' : 'Active');

const ActiveEditor = ({ value, onValueChange }) => (
  <Checkbox
    checked={value !== false}
    onChange={(event) => onValueChange(event.target.checked)}
    inputProps={{ 'aria-label': 'QBO Active' }}
  />
);

const ActiveProvider = (props) => (
  <DataTypeProvider formatterComponent={ActiveFormatter} editorComponent={ActiveEditor} {...props} />
);

const ItemFocusableCell = ({ onClick, ...restProps }) => {
  if (restProps.column.name === 'itemImageUrl') {
    return (
      <Table.Cell {...restProps} value={<img src={restProps.value} style={{ width: '30px', height: '30px' }} />} />
    );
  }

  const handleFocus = (event) => {
    if (event.currentTarget !== event.target) return;
    onClick();
  };

  return <Table.Cell {...restProps} tabIndex={0} onFocus={handleFocus} />;
};

const ImageProvider = (props) => {
  return <DataTypeProvider formatterComponent={ImageFullScreenDialog} {...props} />;
};

const getRowId = (row) => row.id;

// A single click is handled by TableInlineCellEditing. Double-clicking a data
// row clears the cell editor and opens the complete item dialog.
const ClickableItemRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.Name || 'item'} details` : undefined}
      style={{
        ...restProps.style,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

export default function ItemTable({
  rows,
  setRows,
  selection,
  setSelection,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  pageSizes,
  totalCount,
  itemLoading,
  actionLoading,
  searchValue,
  handleInputChange,
  columnFilters,
  setColumnFilters,
  onRefreshItems,
  onDeleteItems,
  onMakeActiveItems,
  onDisableItems,
  onEnableItems
}) {
  const [columns] = useState([
    { name: 'Name', title: 'Name' },
    { name: 'Active', title: 'QBO Active' },
    { name: 'itemStatus', title: 'Verd Active' },
    { name: 'id', title: 'ItemId' },
    { name: 'Description', title: 'Description' },
    { name: 'FullyQualifiedName', title: 'FullyQualifiedName' },
    { name: 'Sku', title: 'Sku' },

    { name: 'Taxable', title: 'Taxable' },
    { name: 'UnitPrice', title: 'UnitPrice' },
    { name: 'Type', title: 'Type' },

    { name: 'SalesTaxIncluded', title: 'SalesTaxIncluded' },
    { name: 'PurchaseDesc', title: 'PurchaseDesc' },
    { name: 'PurchaseTaxIncluded', title: 'PurchaseTaxIncluded' },
    { name: 'PurchaseCost', title: 'PurchaseCost' },

    {
      name: 'PurchaseTaxCodeRef',
      title: 'PurchaseTaxCodeRef',
      getCellValue: (row) => (row.PurchaseTaxCodeRef ? row?.PurchaseTaxCodeRef?.name : undefined)
    },
    {
      name: 'SalesTaxCodeRef',
      title: 'SalesTaxCodeRef',
      getCellValue: (row) => (row.SalesTaxCodeRef ? row?.SalesTaxCodeRef?.name : undefined)
    },
    { name: 'ClassRef', title: 'ClassRef', getCellValue: (row) => (row.ClassRef ? row?.ClassRef?.name : undefined) },
    {
      name: 'ParentRef',
      title: 'ParentRef',
      getCellValue: (row) => (row.ParentRef ? row?.ParentRef?.name : undefined)
    },
    {
      name: 'PrefVendorRef',
      title: 'PrefVendorRef',
      getCellValue: (row) => (row.PrefVendorRef ? row?.PrefVendorRef?.name : undefined)
    },
    {
      name: 'IncomeAccountRef',
      title: 'IncomeAccountRef',
      getCellValue: (row) => (row.IncomeAccountRef ? row?.IncomeAccountRef?.name : undefined)
    },
    {
      name: 'ExpenseAccountRef',
      title: 'ExpenseAccountRef',
      getCellValue: (row) => (row.ExpenseAccountRef ? row?.ExpenseAccountRef?.name : undefined)
    },

    { name: 'TrackQtyOnHand', title: 'TrackQtyOnHand' },
    { name: 'SyncToken', title: 'SyncToken' },
    { name: 'itemImageUrl', title: 'ItemImage' }
  ]);

  //it was using useLocalStorage itemColumnWidths, update it once item columns are done
  const [itemColumnWidths, setItemColumnWidths] = useState([
    { columnName: 'Name', width: 180 },
    { columnName: 'Active', width: 130 },
    { columnName: 'itemStatus', width: 180 },
    { columnName: 'id', width: 180 },
    { columnName: 'Description', width: 180 },
    { columnName: 'FullyQualifiedName', width: 180 },
    { columnName: 'Sku', width: 180 },

    { columnName: 'Taxable', width: 180 },
    { columnName: 'UnitPrice', width: 180 },
    { columnName: 'Type', width: 180 },

    { columnName: 'SalesTaxIncluded', width: 180 },
    { columnName: 'PurchaseDesc', width: 180 },
    { columnName: 'PurchaseTaxIncluded', width: 180 },
    { columnName: 'PurchaseCost', width: 180 },

    { columnName: 'PurchaseTaxCodeRef', width: 180 },
    { columnName: 'SalesTaxCodeRef', width: 180 },
    { columnName: 'ClassRef', width: 180 },
    { columnName: 'ParentRef', width: 180 },
    { columnName: 'PrefVendorRef', width: 180 },

    { columnName: 'IncomeAccountRef', width: 180 },
    { columnName: 'ExpenseAccountRef', width: 180 },

    { columnName: 'TrackQtyOnHand', width: 180 },
    { columnName: 'SyncToken', width: 180 },
    { columnName: 'itemImageUrl', width: 180 }
  ]);
  const [itemColumnOrder, setItemColumnOrder] = useState([
    'FullyQualifiedName',
    'Name',
    'Active',
    'itemStatus',
    'id',
    'Description',
    'Sku',

    'Taxable',
    'UnitPrice',
    'Type',

    'PurchaseDesc',
    'SalesTaxIncluded',
    'PurchaseTaxIncluded',
    'PurchaseCost',

    'PurchaseTaxCodeRef',
    'SalesTaxCodeRef',
    'ClassRef',
    'ParentRef',
    'PrefVendorRef',

    'IncomeAccountRef',
    'ExpenseAccountRef',

    'TrackQtyOnHand',
    'SyncToken',
    'itemImageUrl'
  ]);

  const [sorting, setSorting] = useState([{ columnName: 'FullyQualifiedName', direction: 'asc' }]);
  const [hiddenColumnNames, setHiddenColumnNames] = useState(['']);

  const itemImageUrl = ['itemImageUrl'];

  const editingModeRef = useRef(null);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);

  const changeEditingMode = useCallback((mode) => {
    editingModeRef.current = mode;
  }, []);

  const startPopupEditing = useCallback(() => {
    setEditingCellIds([]);
    changeEditingMode('popup');
  }, [changeEditingMode]);

  const openItemDetails = useCallback(
    (rowId) => {
      setEditingCellIds([]);
      changeEditingMode('popup');
      setEditingRowIds([rowId]);
    },
    [changeEditingMode]
  );

  const editingStateColumnExtensions = useMemo(
    () => columns.map(({ name }) => ({ columnName: name, editingEnabled: ITEM_INLINE_COLUMNS.includes(name) })),
    [columns]
  );

  const commitChanges = ({ changed, deleted }) => {
    if (deleted?.length) {
      onDeleteItems(deleted);
    }
    if (!changed || Object.keys(changed).length === 0) return;

    const meaningfulChanges = Object.entries(changed).reduce((result, [rowId, rowChanges]) => {
      const originalRow = rows.find((row) => String(row.id) === String(rowId));
      if (!originalRow) return result;
      const itemChanges = meaningfulItemChanges(originalRow, rowChanges);
      return Object.keys(itemChanges).length ? { ...result, [rowId]: itemChanges } : result;
    }, {});
    if (Object.keys(meaningfulChanges).length === 0) return;

    const previousRows = rows;
    const changedRows = rows.map((row) => (meaningfulChanges[row.id] ? { ...row, ...meaningfulChanges[row.id] } : row));

    const updates = Object.keys(meaningfulChanges).map((rowId) => {
      const originalRow = rows.find((row) => String(row.id) === String(rowId));
      if (!originalRow) throw new Error(`Item row ${rowId} was not found`);

      // eslint-disable-next-line no-undef
      const formData = new FormData();
      formData.append('updateItemPayload', JSON.stringify(sparseItemUpdate(originalRow, meaningfulChanges[rowId])));
      // eslint-disable-next-line no-undef
      formData.append('userId', localStorage.getItem('userId'));
      return itemService.updateItemInQbo(formData);
    });

    setRows(changedRows);

    // eslint-disable-next-line no-undef
    Promise.all(updates)
      .then((responses) => {
        // eslint-disable-next-line no-undef
        const savedItems = new Map(
          responses
            .map((response) => response?.data?.data)
            .filter(Boolean)
            .map((item) => [String(item.Id), item])
        );
        setRows((current) =>
          current.map((row) => {
            const saved = savedItems.get(String(row.id));
            return saved ? { ...row, ...saved, id: saved.Id } : row;
          })
        );
        toast.success('Successfully updated item');
      })
      .catch((error) => {
        setRows(previousRows);
        const qboMessage = error?.response?.data?.message?.Fault?.Error?.[0]?.Detail;
        const message = error?.response?.data?.message;
        toast.error(typeof message === 'string' ? message : qboMessage || error?.message || 'Could not update item');
      });
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Column header filters
      for (const columnName of Object.keys(columnFilters)) {
        // QBO Active is filtered by the API so pagination and totals cover the
        // full QuickBooks catalogue rather than only the current page.
        if (columnName === 'Active') continue;
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [rows, columnFilters]);

  const exporterRef = useRef(null);

  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  const ToolbarRoot = useCallback(
    (props) => (
      <CustomToolbarRoot
        {...props}
        total={totalCount}
        selectedCount={selection.length}
        refreshing={itemLoading}
        actionLoading={actionLoading}
        onRefresh={onRefreshItems}
        onDelete={onDeleteItems}
        onMakeActive={onMakeActiveItems}
        onDisable={onDisableItems}
        onEnable={onEnableItems}
      />
    ),
    [
      actionLoading,
      itemLoading,
      onDeleteItems,
      onMakeActiveItems,
      onDisableItems,
      onEnableItems,
      onRefreshItems,
      selection.length,
      totalCount
    ]
  );

  return (
    <div>
      <HeaderFilterContext.Provider
        value={{
          rows,
          columnFilters,
          columnValues: ITEM_FILTER_VALUES,
          onFilterChange: setColumnFilters
        }}
      >
        <Paper style={{ position: 'relative', minWidth: 0 }} aria-busy={itemLoading}>
          <Grid rows={filteredRows} columns={columns} getRowId={getRowId}>
            <EditingState
              editingCellIds={editingCellIds}
              onEditingCellIdsChange={(cellIds) => {
                if (editingModeRef.current !== 'popup' || editingModeRef.current === null) {
                  const editableCellIds = cellIds.filter(({ columnName }) => ITEM_INLINE_COLUMNS.includes(columnName));
                  changeEditingMode(editableCellIds.length ? 'inline' : null);
                  setEditingCellIds(editableCellIds);
                }
              }}
              editingRowIds={editingRowIds}
              onEditingRowIdsChange={(rowIds) => {
                setEditingRowIds(rowIds);
                if (rowIds.length) {
                  setEditingCellIds([]);
                  changeEditingMode('popup');
                } else if (editingModeRef.current === 'popup') {
                  changeEditingMode(null);
                }
              }}
              onCommitChanges={commitChanges}
              columnExtensions={editingStateColumnExtensions}
            />
            <PagingState
              currentPage={currentPage}
              onCurrentPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
            <SelectionState selection={selection} onSelectionChange={setSelection} />
            <IntegratedSelection />

            <DragDropProvider />

            <SortingState sorting={sorting} onSortingChange={setSorting} />
            {!searchValue.trim() && <IntegratedSorting />}

            <SearchState value={searchValue} onValueChange={handleInputChange} />
            {/* <IntegratedPaging /> */}
            <CustomPaging totalCount={totalCount} />

            <ImageProvider for={itemImageUrl} />
            <ActiveProvider for={['Active']} />

            <Table
              rowComponent={(props) => <ClickableItemRow {...props} onOpen={openItemDetails} />}
              cellComponent={ItemFocusableCell}
              containerComponent={ResponsiveTableContainer}
              headComponent={StickyTableHead}
              messages={{ noData: itemLoading ? 'Loading items…' : 'No items found' }}
            />
            <TableColumnResizing columnWidths={itemColumnWidths} onColumnWidthsChange={setItemColumnWidths} />
            <TableColumnReordering order={itemColumnOrder} onOrderChange={setItemColumnOrder} />

            <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />

            <TableSelection showSelectAll />
            <PagingPanel pageSizes={pageSizes} />

            {/* Native grid edit commands, displayed through the shared KebabCommandCell. */}
            <TableEditColumn
              width={90}
              showEditCommand
              showDeleteCommand
              cellComponent={(props) => <KebabCommandCell {...props} onPopupEditStart={startPopupEditing} />}
            />

            <TableInlineCellEditing startEditAction="click" selectTextOnEditStart={true} />

            <PopupEditing popupComponent={Popup} />
            <TableColumnVisibility
              hiddenColumnNames={hiddenColumnNames}
              onHiddenColumnNamesChange={setHiddenColumnNames}
            />

            <Toolbar rootComponent={ToolbarRoot} />

            <SearchPanel messages={{ searchPlaceholder: 'Search description, purchase description, name or ID' }} />
            <ColumnChooser />
            <ExportPanel startExport={startExport} />
          </Grid>
          <GridExporter
            ref={exporterRef}
            columns={columns}
            rows={filteredRows}
            selection={selection}
            onSave={onSave}
            getRowId={getRowId}
            hiddenColumnNames={hiddenColumnNames}
          />
          {itemLoading && (
            <div
              role="progressbar"
              aria-label={searchValue ? 'Searching items' : 'Loading items'}
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.7)'
              }}
            >
              <CircularProgress />
            </div>
          )}
        </Paper>
      </HeaderFilterContext.Provider>
    </div>
  );
}
